/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { FaCaretDown } from "react-icons/fa6";
import { MdLanguage } from "react-icons/md";
import { LANGUAGES } from "./constants";
import { useLanguageDropdown } from "@/hooks/useLanguageDropdown";
// P-4 (Session 376): the control carries its own appearance. These rules used
// to live in header.css, which only the pages with a site header import — so
// mounting this component anywhere else rendered it as unstyled text, with
// nothing in the type system or the tests noticing. Do not remove this import.
import "@/static/css/common/languageSwitcher.css";

interface LanguageSwitcherProps {
  selectedLanguage: string;
  onLanguageChange: (lang: string) => void;
}

/**
 * The site's one language control.
 *
 * P-4 (Session 376) made this the only copy. There used to be three: this one,
 * a near-identical `AuthLanguageSwitcher` on the sign-in pages, and a
 * hand-written one on the uninstall-feedback page that reused these class names
 * without the shared hook — so it announced itself as a listbox while offering
 * neither Escape, nor arrow keys, nor focus return. Both are gone; this
 * component is mounted in all three places.
 */
const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  selectedLanguage,
  onLanguageChange,
}) => {
  const { t } = useTranslation();
  // T-3: the open/close, focus and keyboard behaviour moved into a shared hook
  // so the sign-in pages' own language control cannot drift away from this one.
  // Nothing about this component's appearance or markup changed.
  const {
    open: dropdownVisible,
    setOpen: setDropdownVisible,
    containerRef: dropdownRef,
    triggerRef,
    select: handleSelect,
    onTriggerKeyDown,
    onListKeyDown: handleDropdownKeyDown,
  } = useLanguageDropdown(onLanguageChange);

  /* F-8 (Session 393): the fallback was the hardcoded English string
     "Select Language" — the one string in this component that was not
     translated. The key exists in all six language files. */
  const currentLanguage =
    LANGUAGES.find((lang) => lang.id === selectedLanguage)?.name ??
    t("accessibility.select_language");

  return (
    <div className="select-language" ref={dropdownRef}>
      {/* WCAG SC 4.1.2: the button needs an accessible name that conveys its
          purpose — and, F-8 (Session 393), the value it is set to.

          An aria-label REPLACES the button's own content when a screen reader
          works out its name, so the visible word inside it, "English" or
          "Deutsch", was never read. The control announced what it is for and
          never what it is set to, at every width. Above 1131px, where the name
          is on screen, an accessible name that does not contain the visible
          label is a Level A failure of SC 2.5.3 Label in Name in its own
          right, and it is why somebody driving the site by voice who says
          "click English" got nothing. Below 1131px the visible name is hidden
          as well, so the control was a wordless globe that neither showed nor
          announced the current language. */}
      <button
        ref={triggerRef}
        onClick={() => setDropdownVisible(!dropdownVisible)}
        onKeyDown={onTriggerKeyDown}
        className={dropdownVisible ? "lang-box-clicked" : "lang-box"}
        aria-haspopup="listbox"
        aria-expanded={dropdownVisible}
        aria-label={`${t("accessibility.select_language")}: ${currentLanguage}`}
        type="button"
      >
        <MdLanguage className="language-icon" />
        <p>{currentLanguage}</p>
        <FaCaretDown
          className="svg-inline--fa fa-caret-down language-dropdown"
          style={dropdownVisible ? { rotate: "180deg" } : { rotate: "unset" }}
        />
      </button>
      {dropdownVisible && (
        <div
          className="dropdown"
          role="listbox"
          tabIndex={-1}
          aria-label={t("accessibility.select_language")}
          onKeyDown={handleDropdownKeyDown}
        >
          {LANGUAGES.map((lang) => (
            <button
              style={
                selectedLanguage === lang.id
                  ? { backgroundColor: "#ebebeb" }
                  : { backgroundColor: "white" }
              }
              key={lang.id}
              onClick={() => handleSelect(lang.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleSelect(lang.id);
                }
              }}
              role="option"
              aria-selected={selectedLanguage === lang.id}
              type="button"
            >
              {lang.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
